import useStoreFilter from './useStoreFilter';
import { useEffect, useState } from 'react';
import ScoreSelect from './ScoreSelect';
import { QuickRangePicker } from 'components/QuickRangePicker';
import dayjs, { Dayjs } from 'dayjs';
import { useRequest } from 'ahooks';
import { exportCsatList, getCsatList } from './service';
import { Button, Input, message } from 'antd';
import CsatDetailTable, { useLabels } from './CsatDetailTable';
import { useExportFile } from 'components/ExportCenter/hooks/useExportCenter';
import { DownloadOutlined } from '@ant-design/icons';
import { getLocalTimeZone } from 'utils/timeZone';
import { useGetPermission } from 'hooks/business/useNewPermisson';
import styled from '@emotion/styled';
import { eventBus } from 'services/eventBus';
import { StoreInfo } from 'services/api/dashboard';
import { uniq } from 'lodash-es';
import { list } from 'services/api/assistant.account';
import SearchSelect from './SearchSelect';

function CsatDetail({
  platform,
  departments,
  initStoreInfo,
  onCheckConversation,
}: {
  platform: General.PlatformUpperCase;
  departments: Dashboard.Department[];
  initStoreInfo?: StoreInfo;
  onCheckConversation: (record: Dashboard.PlatformData.CsatData) => void;
}) {
  const { t, scoreSourceLabelMap, receptionModeLabelMap } = useLabels();

  const {
    selectedDepartments,
    departmentsSelect,
    setSelectedDepartments,
    storeType,
    storeTypeSelect,
    setStoreType,
    storeInfo,
    storeInfoSelect,
    setStoreInfo,
  } = useStoreFilter({
    platform,
    departments,
    initStoreInfo,
  });

  const [score, setScore] = useState<number[]>();

  const scoreSelect = <ScoreSelect platform={platform} value={score} onChange={setScore} />;

  const [buyerNickValue, setBuyerNickValue] = useState('');
  const [buyerNick, setBuyerNick] = useState(buyerNickValue);

  const buyerNickSearch = (
    <Input
      placeholder={t('买家昵称')}
      value={buyerNickValue}
      onChange={({ target: { value } }) => setBuyerNickValue(value)}
      onBlur={() => setBuyerNick(buyerNickValue)}
      onPressEnter={() => setBuyerNick(buyerNickValue)}
    />
  );

  const [scoreSources, setScoreSources] = useState<Dashboard.PlatformData.ScoreSource[]>([]);

  const scoreSourcesSelect = (
    <SearchSelect
      placeholder={t('评分来源')}
      value={scoreSources}
      onChange={setScoreSources}
      options={Object.entries(scoreSourceLabelMap).map(([value, label]) => ({ value, label }))}
    />
  );

  const [receptionModes, setReceptionModes] = useState<Dashboard.PlatformData.ReceptionMode[]>([]);

  const receptionModesSelect = (
    <SearchSelect
      placeholder={t('接待方式')}
      value={receptionModes}
      onChange={setReceptionModes}
      options={Object.entries(receptionModeLabelMap).map(([value, label]) => ({ value, label }))}
    />
  );

  const { loading: assistantLoading, data: assistantOptions } = useRequest(() => {
    return list({ page: 1, pageSize: 1000 }).then((res) => {
      return res.data?.map(({ id, name, account }) => {
        return {
          value: id,
          label: name || account,
        };
      });
    });
  });

  const [lastReceptionAssistantIds, setLastReceptionAssistantIds] = useState<string[]>([]);

  const lastReceptionAssistantIdsSelect = (
    <SearchSelect
      showSearch
      loading={assistantLoading}
      placeholder={t('最后接待客服')}
      value={lastReceptionAssistantIds}
      onChange={setLastReceptionAssistantIds}
      options={assistantOptions}
    />
  );

  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().subtract(30, 'day').startOf('day'),
    dayjs().subtract(1, 'day').endOf('day'),
  ]);

  const dateRangeSelect = (
    <QuickRangePicker
      quickActions={[
        {
          key: 'last7days',
          label: t('remind.近x天', { x: 7 }),
          range: [
            dayjs().subtract(7, 'day').startOf('day'),
            dayjs().subtract(1, 'day').endOf('day'),
          ],
        },
        {
          key: 'last30days',
          label: t('remind.近x天', { x: 30 }),
          range: [
            dayjs().subtract(30, 'day').startOf('day'),
            dayjs().subtract(1, 'day').endOf('day'),
          ],
        },
      ]}
      value={dateRange}
      onChange={(range) => {
        const [start, end] = range || [];
        if (start && end) {
          setDateRange([start, end]);
        }
      }}
    />
  );

  const filter = (
    <header>
      <section>
        {departmentsSelect}
        {storeTypeSelect}
        {storeInfoSelect}
        {scoreSelect}
        {buyerNickSearch}
        {scoreSourcesSelect}
        {receptionModesSelect}
        {lastReceptionAssistantIdsSelect}
        <Button
          type="link"
          onClick={() => {
            setSelectedDepartments([]);
            setStoreType(undefined);
            setStoreInfo({});
            setScore([]);
            setBuyerNick('');
            setBuyerNickValue('');
            setScoreSources([]);
            setReceptionModes([]);
            setLastReceptionAssistantIds([]);
          }}
        >
          {t('重置筛选')}
        </Button>
      </section>
      {dateRangeSelect}
    </header>
  );

  const [pageSize, setPageSize] = useState(10);

  const { loading, data, run } = useRequest(
    (
      page: {
        pageNum: number;
        pageSize: number;
      } = {
        pageNum: 1,
        pageSize,
      },
    ) => {
      return getCsatList({
        ...page,
        platform,
        departmentIds: selectedDepartments.map(({ id }) => id),
        storeType: storeType === 'CROSS' ? '跨境' : storeType === 'LOCAL' ? '本土' : undefined,
        storeIds: storeInfo.list?.map(({ id }) => Number(id)),
        scoreList: score?.includes(6) ? uniq([1, 2, 3, ...score]).filter((_) => _ !== 6) : score,
        buyerNick,
        scoreSources,
        receptionModes,
        lastReceptionAssistantIds,
        startTimeStamp: dateRange[0].valueOf().toString(),
        endTimeStamp: dateRange[1].endOf('days').valueOf().toString(),
      });
    },
    {
      manual: true,
      onError: () => message.error(t('phrase.获取数据失败，请稍后再试')),
    },
  );

  useEffect(() => {
    run();
  }, [
    run,
    selectedDepartments,
    storeType,
    storeInfo,
    score,
    dateRange,
    buyerNick,
    scoreSources,
    receptionModes,
    lastReceptionAssistantIds,
  ]);

  const table = (
    <CsatDetailTable
      platform={platform}
      onCheckConversation={onCheckConversation}
      loading={loading}
      dataSource={data?.list}
      pagination={{
        current: data?.pageNum || 1,
        pageSize: data?.pageSize || pageSize,
        total: data?.total,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (x) => t('feedback.共x项', { x }),
        onChange: (pageNum, pageSize) => {
          setPageSize(pageSize);
          run({
            pageNum,
            pageSize,
          });
        },
      }}
    />
  );

  const { run: onDownload } = useExportFile();

  const exportable = useGetPermission()('导出平台数据');

  useEffect(() => {
    const handler = (storeInfo: StoreInfo) => {
      if (storeInfo.platform !== platform) {
        return;
      }
      setPageSize(10);
      setSelectedDepartments([]);
      setStoreType(undefined);
      setStoreInfo({ list: [storeInfo] });
      setDateRange([
        dayjs().subtract(30, 'day').startOf('day'),
        dayjs().subtract(1, 'day').endOf('day'),
      ]);
    };

    eventBus.on('CHECK_STORE_PLATFORM_CSAT_DETAIL', handler);

    return () => eventBus.off('CHECK_STORE_PLATFORM_CSAT_DETAIL', handler);
  }, [platform, setSelectedDepartments, setStoreType, setStoreInfo]);

  return (
    <Container>
      {filter}
      <section>
        <header>
          <h3>{t('CSAT明细')}</h3>
          {exportable && (
            <Button
              icon={<DownloadOutlined />}
              onClick={() => {
                onDownload(() =>
                  exportCsatList({
                    platform,
                    departmentIds: selectedDepartments.map(({ id }) => id),
                    storeType:
                      storeType === 'CROSS' ? '跨境' : storeType === 'LOCAL' ? '本土' : undefined,
                    storeIds: storeInfo.list?.map(({ id }) => Number(id)),
                    scoreList: score?.includes(6)
                      ? uniq([1, 2, 3, ...score]).filter((_) => _ !== 6)
                      : score,
                    buyerNick: buyerNickValue,
                    scoreSources,
                    receptionModes,
                    lastReceptionAssistantIds,
                    startTimeStamp: dateRange[0].valueOf().toString(),
                    endTimeStamp: dateRange[1].endOf('days').valueOf().toString(),
                    timeZone: getLocalTimeZone(),
                  }),
                );
              }}
            >
              {t('导出详细数据')}
            </Button>
          )}
        </header>
        <div>{table}</div>
      </section>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  > header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 16px;
    margin-bottom: 16px;
    margin-top: 1px;
    background: var(--fett-bg-panel);
    > section {
      flex: 1 0 0;
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 8px 16px;
      > input {
        width: 160px;
      }
    }
  }
  > section {
    flex: 1 0 0;
    display: flex;
    flex-direction: column;
    > header {
      flex: 0 0 auto;
      display: flex;
      justify-content: space-between;
      align-items: center;
      height: 56px;
      padding: 0 16px;
      margin-bottom: 1px;
      background: var(--fett-bg-panel);
      > h3 {
        margin-bottom: 0;
      }
    }
    > div {
      flex: 1 0 0;
      padding: 16px;
      background: var(--fett-bg-panel);
    }
  }
`;

export default CsatDetail;
